import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { ScrollMatrixSubject } from '@Pages/Heatmap/HeatmapObservableSubjects';
import scrollUtils from '@Pages/Heatmap/HeatmapTable/scrollUtils';
import Scrollable from '@Pages/Heatmap/HeatmapTable/Matrix/Scrollable';
import Table from '@Pages/Heatmap/HeatmapTable/Matrix/Table';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';

const Matrix = forwardRef(
  (
    { values, viewOptions, onInitData, onRequestData, handleHover, hoveredRow, hoveredColumn, selectedRows, selectedColumns, loaded },
    selfRef
  ) => {
    const scrollbarRef = useRef();
    const [rowStart, setRowStart] = useState(0);
    const [rowsToShow, setRowsToShow] = useState(0);
    const [tableOffsetTop, setTableOffsetTop] = useState(0);
    const [cellStart, setCellStart] = useState(0);
    const [cellsToShow, setCellsToShow] = useState(0);
    const [tableOffsetLeft, setTableOffsetLeft] = useState(0);
    const [matrixWidth, setMatrixWidth] = useState(0);
    const [matrixHeight, setMatrixHeight] = useState(0);

    const checkContainerLimits = useCallback(
      (newTopOffset, newLeftOffset, newRowStart, newCellStart) => {
        let _newTopOffset = newTopOffset;
        let _newLeftOffset = newLeftOffset;
        let _newRowStart = newRowStart;
        let _newCellStart = newCellStart;

        if (newRowStart + rowsToShow >= viewOptions.rowsAmount) {
          _newTopOffset -= (newRowStart - (viewOptions.rowsAmount - rowsToShow)) * viewOptions.cellHeight;
          _newRowStart = viewOptions.rowsAmount - rowsToShow;
        }

        if (newRowStart < 0) {
          _newTopOffset = 0;
          _newRowStart = 0;
        }

        if (newCellStart + cellsToShow >= viewOptions.colsAmount) {
          _newLeftOffset -= (newCellStart - (viewOptions.colsAmount - cellsToShow)) * viewOptions.cellWidth;
          _newCellStart = viewOptions.colsAmount - cellsToShow;
        }

        if (newCellStart < 0) {
          _newLeftOffset = 0;
          _newCellStart = 0;
        }

        return {
          newTopOffset: _newTopOffset,
          newLeftOffset: _newLeftOffset,
          newRowStart: _newRowStart,
          newCellStart: _newCellStart,
        };
      },
      [viewOptions, rowsToShow, cellsToShow]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedScrollHandler = useCallback(
      debounce((scrollTop, scrollLeft) => {
        if (!rowsToShow || !cellsToShow) return;

        const firstRow = scrollUtils.calculateFirstRow(scrollTop, viewOptions.cellHeight);
        const firstRowCeil = Math.ceil(firstRow);
        const rowsOnViewportHalf = Math.ceil(matrixHeight / viewOptions.cellHeight / 2);
        const _newRowStart = firstRowCeil - Math.ceil(rowsToShow / 2) + rowsOnViewportHalf;
        const _newTopOffset =
          scrollTop -
          (Math.ceil(rowsToShow / 2) - firstRowCeil + firstRow - rowsOnViewportHalf) * scrollUtils.calculateCellHeight(viewOptions);
        const firstCell = scrollUtils.calculateFirstCell(scrollLeft, viewOptions.cellWidth);
        const firstCellCeil = Math.ceil(firstCell);
        const cellsOnViewportHalf = Math.ceil(matrixWidth / viewOptions.cellWidth / 2);
        const _newCellStart = firstCellCeil - Math.ceil(cellsToShow / 2) + cellsOnViewportHalf;
        const _newLeftOffset =
          scrollLeft -
          (Math.ceil(cellsToShow / 2) - firstCellCeil + firstCell - cellsOnViewportHalf) * scrollUtils.calculateCellWidth(viewOptions);

        const { newLeftOffset, newTopOffset, newCellStart, newRowStart } = checkContainerLimits(
          _newTopOffset,
          _newLeftOffset,
          _newRowStart,
          _newCellStart
        );

        scrollUtils.setScrollTop(scrollTop);
        scrollUtils.setScrollLeft(scrollLeft);

        setTableOffsetLeft(newLeftOffset);
        setTableOffsetTop(newTopOffset);
        setCellStart(newCellStart);
        setRowStart(newRowStart);

        Logger.debug('matrix request data:', { newRowStart, newCellStart, rowsToShow, cellsToShow });
        onRequestData(newRowStart, newCellStart, rowsToShow, cellsToShow);
      }, viewOptions.scrollDebounce),
      [rowsToShow, cellsToShow, matrixWidth, matrixHeight, viewOptions.cellWidth, viewOptions.cellHeight]
    );

    const terminateComponentSize = useCallback(() => {
      const { offsetWidth: containerWidth, offsetHeight: containerHeight } = selfRef.current;
      let _rowsToShow = Math.ceil(containerHeight / viewOptions.cellHeight) * viewOptions.viewBufferSize;
      let _cellsToShow = Math.ceil(containerWidth / viewOptions.cellWidth) * viewOptions.viewBufferSize;

      if (_rowsToShow > viewOptions.rowsAmount) {
        _rowsToShow = viewOptions.rowsAmount;
      }

      if (_cellsToShow > viewOptions.colsAmount) {
        _cellsToShow = viewOptions.colsAmount;
      }

      setMatrixWidth(containerWidth);
      setMatrixHeight(containerHeight);
      setRowsToShow(_rowsToShow);
      setCellsToShow(_cellsToShow);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewOptions.cellHeight, viewOptions.cellWidth, viewOptions.colsAmount, viewOptions.rowsAmount, viewOptions.viewBufferSize]);

    useEffect(() => {
      if (rowsToShow && cellsToShow) {
        onInitData(rowsToShow, cellsToShow);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellsToShow, rowsToShow]);

    const scrollHandler = useCallback(
      (scrollTop, scrollLeft) => {
        ScrollMatrixSubject.next({ scrollTop, scrollLeft });
        debouncedScrollHandler(scrollTop, scrollLeft);
      },
      [debouncedScrollHandler]
    );

    useEffect(() => {
      const { clientHeight, clientWidth } = scrollbarRef.current;
      scrollUtils.viewportHeight = clientHeight;
      scrollUtils.viewportWidth = clientWidth;

      terminateComponentSize();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { cellWidth, cellHeight, rowsAmount, colsAmount, colsHeaderHeight, rowsHeaderWidth } = viewOptions;
    const containerHeight = rowsAmount * cellHeight;
    const containerWidth = colsAmount * cellWidth;
    const tableWrapper = {
      top: `${tableOffsetTop}px`,
      left: `${tableOffsetLeft}px`,
    };
    const matrixOffset = {
      top: colsHeaderHeight,
      left: rowsHeaderWidth,
    };
    const matrixBackground = {
      paddingRight: `${containerWidth}px`,
      paddingBottom: `${containerHeight}px`,
      backgroundColor: 'transparent',
      backgroundSize: `${cellHeight}px ${cellWidth}px`,
      backgroundImage: 'linear-gradient(to right, #ebebeb 3px, transparent 3px), linear-gradient(to bottom, #ebebeb 3px, transparent 3px)',
    };

    return (
      <div className="heatmap__matrix" style={matrixOffset} ref={selfRef}>
        <Scrollable onScroll={scrollHandler} containerHeight={containerHeight} containerWidth={containerWidth} ref={scrollbarRef}>
          <div className="heatmap__matrix__wrapper" style={tableWrapper}>
            <Table
              data={values}
              cellWidth={cellWidth}
              cellHeight={cellHeight}
              startRow={rowStart}
              startCell={cellStart}
              rowsToRender={rowsToShow}
              cellsToRender={cellsToShow}
              selectedRows={selectedRows}
              selectedColumns={selectedColumns}
              handleHover={handleHover}
              hoveredColumn={hoveredColumn}
              hoveredRow={hoveredRow}
              totalRows={viewOptions.rowsAmount}
              totalColumns={viewOptions.colsAmount}
            />
          </div>
          <div className="heatmap__matrix__background" style={matrixBackground} />
        </Scrollable>
        {!loaded && <SimpleSpinner />}
      </div>
    );
  }
);

Matrix.displayName = 'Matrix';

Matrix.propTypes = {
  values: PropTypes.object.isRequired,
  viewOptions: PropTypes.object.isRequired,
  onInitData: PropTypes.func.isRequired,
  onRequestData: PropTypes.func.isRequired,
  handleHover: PropTypes.func.isRequired,
  hoveredRow: PropTypes.object,
  hoveredColumn: PropTypes.object,
  selectedRows: PropTypes.array,
  selectedColumns: PropTypes.array,
  loaded: PropTypes.bool,
};

export default Matrix;
